import { useState } from "react";

import type { K } from "@/api/ozet";
import type { SiteKpi, SiteKpiBreakdownItem, SiteMetrikleriResponse } from "@/api/siteMetrikleri";
import { EmptyState } from "@/components/EmptyState";
import { KpiPeriodSelect } from "@/components/KpiPeriodSelect";
import { Pill, SectionHeader } from "@/components/ui";
import { DeltaBadge } from "@/features/ozet/GidisatHero";
import type { DeltaMode } from "@/hooks/useDeltaMode";
import { t, type TrKey } from "@/i18n";
import { fmtFloat, fmtInt, fmtMoney, fmtPct } from "@/lib/utils";

/**
 * "Site Metrikleri" bölümü — backend `GET /api/v2/kpi/site-metrikleri`
 * (`app/ga4/transformers.py build_kpi_snapshot`) 4 sabit kategori döner.
 *
 * Tasarım (17.09.2026): her kategori eşit yükseklikte kartlar; kırılım tablosu
 * kartın içine gömülü DEĞİL, "kırılım ▾" ile açılır; bağlı olmayan kartlar
 * kategori altında tek satırlık kapalı bir listede durur (bkz.
 * docs/arayuz-tasarim-rehberi.md §2).
 */

interface SiteMetrikleriProps {
  data: SiteMetrikleriResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  deltaMode: DeltaMode;
  /** Kart tıklanabilirse (bkz. DETAIL_KPI_MAP) — kaynak KPI id'sini açar. */
  onOpenDetail: (detailKpiId: string, title: string) => void;
}

/** Site Metrikleri kart id'si → `/kpi/{id}/items` loader'ı olan asıl kart id'si. */
const DETAIL_KPI_MAP: Record<string, string> = {
  top_products: "encok_satan",
  bekleyen_iade_sayisi: "bekleyen_iade",
  today_orders: "bugunku_siparis",
  today_revenue: "bugunku_siparis",
  stok_disi_orani: "kritik_stok",
  siparis_hazirlik_suresi: "kargolanmamis",
  kargo_teslim_suresi: "kargo_yolda",
  destek_cozum_suresi: "destek_talepleri",
};

const BREAKDOWN_FIELD_CONFIG: Record<string, { labelKey: TrKey; fmt: (v: number) => string }> = {
  sessions: { labelKey: "kpi.site_metrikleri.alan.sessions", fmt: fmtInt },
  users: { labelKey: "kpi.site_metrikleri.alan.users", fmt: fmtInt },
  count: { labelKey: "kpi.site_metrikleri.alan.count", fmt: fmtInt },
  sold: { labelKey: "kpi.site_metrikleri.alan.sold", fmt: fmtInt },
  revenue: { labelKey: "kpi.site_metrikleri.alan.revenue", fmt: fmtMoney },
};

/** `duration_sec` → "2 dk 46 sn" (saatlik ise "1 sa 05 dk"). */
export function fmtDurationSec(v: number): string {
  const s = Math.max(0, Math.round(v));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h} sa ${String(m).padStart(2, "0")} dk`;
  return `${m} dk ${String(r).padStart(2, "0")} sn`;
}

/** `hours` → "14,7 sa" (≥ 48 sa ise gün cinsinden "2,3 gün"). */
export function fmtHours(v: number): string {
  if (v >= 48) return `${(v / 24).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} gün`;
  return `${v.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} sa`;
}

export function formatKpiValue(value: number | null, format: string): string {
  if (value == null) return "—";
  switch (format) {
    case "currency_try":
      return fmtMoney(value);
    case "percent":
      return fmtPct(value);
    case "float":
      return fmtFloat(value);
    case "duration_sec":
      return fmtDurationSec(value);
    case "hours":
      return fmtHours(value);
    case "integer":
    default:
      return fmtInt(value);
  }
}

/** `comparison.value` (0-1 fraksiyon) → DeltaBadge'in beklediği `K`. */
function kpiDeltaK(kpi: SiteKpi): K | null {
  const comp = kpi.comparison;
  if (!comp || comp.value == null) return null;
  return { cur: kpi.value, prev: comp.previous_value ?? null, delta_pct: comp.value * 100 };
}

function BreakdownTable({ items }: { items: SiteKpiBreakdownItem[] }) {
  if (items.length === 0) return null;
  const valueKeys = Object.keys(items[0]).filter((k) => k !== "name" && k in BREAKDOWN_FIELD_CONFIG);
  return (
    <div className="mt-2 overflow-x-auto">
      <table className="w-full min-w-[220px] text-left text-xs">
        <thead className="text-muted">
          <tr>
            <th scope="col" className="py-0.5 pr-2 font-medium">
              {t("kpi.site_metrikleri.kirilim_ad")}
            </th>
            {valueKeys.map((k) => (
              <th key={k} scope="col" className="py-0.5 pr-2 text-right font-medium">
                {t(BREAKDOWN_FIELD_CONFIG[k].labelKey)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.slice(0, 8).map((item, i) => (
            <tr key={`${String(item.name)}-${i}`} className="border-t border-line">
              <td className="py-1 pr-2 text-ink">{item.name ?? "—"}</td>
              {valueKeys.map((k) => {
                const raw = (item as Record<string, unknown>)[k];
                const n = typeof raw === "number" ? raw : Number(raw);
                return (
                  <td key={k} className="num py-1 pr-2 text-right text-ink">
                    {Number.isFinite(n) ? BREAKDOWN_FIELD_CONFIG[k].fmt(n) : "—"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SiteKpiCard({ kpi, deltaMode, onOpenDetail }: { kpi: SiteKpi; deltaMode: DeltaMode; onOpenDetail: SiteMetrikleriProps["onOpenDetail"] }) {
  const [acik, setAcik] = useState(false);
  const isLive = kpi.status === "live";
  const fmt = (v: number | null | undefined) => formatKpiValue(v ?? null, kpi.format);
  const deltaK = isLive ? kpiDeltaK(kpi) : null;
  const detailId = DETAIL_KPI_MAP[kpi.id];
  const clickable = isLive && detailId != null;
  const valueText = fmt(kpi.value);
  const kirilim = isLive && kpi.breakdown && kpi.breakdown.length > 0 ? kpi.breakdown : null;

  return (
    <div
      className={`kpi flex flex-col ${isLive ? "kpi-ok" : "kpi-off"} ${clickable ? "kpi-clickable" : ""}`}
      data-testid="site-kpi-card"
      data-kpi-id={kpi.id}
      data-status={kpi.status}
      onClick={clickable ? () => onOpenDetail(detailId, kpi.title) : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenDetail(detailId, kpi.title);
              }
            }
          : undefined
      }
    >
      <div className="flex items-start justify-between gap-2">
        <div className="kpi-lbl">{kpi.title}</div>
        {kpi.status === "error" && <Pill tone="bad">{t("empty.error.title")}</Pill>}
        {kpi.status !== "live" && kpi.status !== "error" && <Pill tone="muted">{t("empty.not_connected.title")}</Pill>}
      </div>

      <div className="mt-2 flex items-baseline gap-2">
        <div className="kpi-val" data-testid="kpi-value">
          {valueText}
        </div>
        {deltaK && <DeltaBadge k={deltaK} fmt={fmt} mode={deltaMode} />}
      </div>

      <div className="kpi-sub mt-auto flex items-center justify-between gap-2 pt-2">
        <span className="truncate" title={kpi.source ?? undefined}>
          {kpi.status === "error" && kpi.error ? kpi.error : t("kpi.site_metrikleri.kaynak", { kaynak: kpi.source ?? "—" })}
        </span>
        {kirilim && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setAcik((v) => !v);
            }}
            aria-expanded={acik}
            className="shrink-0 font-semibold text-brand-primary hover:underline"
          >
            {t("kpi.site_metrikleri.kirilim")} {acik ? "▴" : "▾"}
          </button>
        )}
      </div>
      {kirilim && acik && <BreakdownTable items={kirilim} />}
    </div>
  );
}

function BagliOlmayanlar({ kpis }: { kpis: SiteKpi[] }) {
  const [acik, setAcik] = useState(false);
  if (kpis.length === 0) return null;
  return (
    <div className="mt-2 text-xs text-muted">
      <button type="button" onClick={() => setAcik((v) => !v)} aria-expanded={acik} className="font-semibold text-muted hover:text-ink">
        {t("kpi.site_metrikleri.bagli_olmayan", { n: String(kpis.length) })} {acik ? "▴" : "▾"}
      </button>
      {acik && (
        <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
          {kpis.map((k) => (
            <li key={k.id} title={k.source ?? undefined}>
              {k.title}
              {k.source && <span className="opacity-70"> · {k.source}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SiteMetrikleri({ data, isLoading, isError, deltaMode, onOpenDetail }: SiteMetrikleriProps) {
  return (
    <div className="space-y-3" data-testid="site-metrikleri">
      <SectionHeader title={t("kpi.site_metrikleri.baslik")} note={t("kpi.site_metrikleri.alt")} actions={<KpiPeriodSelect />} />

      {isLoading && <p className="text-sm text-muted">{t("common.loading")}</p>}
      {isError && <EmptyState kind="error" />}

      {!isLoading && !isError && data && (
        <div className="space-y-6">
          {data.categories.map((category) => {
            const canli = category.kpis.filter((k) => k.status === "live" || k.status === "error");
            const bagliDegil = category.kpis.filter((k) => k.status !== "live" && k.status !== "error");
            return (
              <section key={category.id} data-testid="kpi-kategori" data-kategori-id={category.id}>
                <h3 className="mb-2 font-display text-[15px] font-bold text-navy">{category.title}</h3>
                {canli.length === 0 && bagliDegil.length > 0 && (
                  <p className="text-sm text-muted">{t("kpi.site_metrikleri.kategori_bos")}</p>
                )}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {canli.map((kpi) => (
                    <SiteKpiCard key={kpi.id} kpi={kpi} deltaMode={deltaMode} onOpenDetail={onOpenDetail} />
                  ))}
                </div>
                <BagliOlmayanlar kpis={bagliDegil} />
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
